import { sum } from 'lodash';
import { HOURS_PER_DAY } from 'src/enums/general';
import { probabilities, simulationChartType } from 'src/enums/simulations';
import { round, displayNumber } from '@oliasoft-open-source/units';
import i18n from 'i18next';
import translations from 'src/internationalisation/translation-map.json';

export const chartKeys = ['chartOne', 'chartTwo', 'chartThree', 'chartFour'];

export const getMean = (risk, tasks) => {
  const { distribution = 'None', min = 0, most = 0, max = 0, withBranch = false } = risk;

  if (withBranch) {
    // Calculate mean from child branches
    const branches = tasks.filter((task) => task.isBranch && task.parentId === risk.taskId);
    return branches.reduce((mean, branch) => mean + branch.certainty * 0.01 * getMean(branch), 0);
  }
  switch (distribution) {
    case 'spike':
      return most;
    case 'uniform':
      return (max + min) / 2;
    case 'pert':
      return (max + 4 * most + min) / 6;
    default:
      return 0;
  }
};

export const sectionObjectKeys = Object.freeze({
  DURATIONS: 'durations',
  COSTS: 'costs',
});
export const getSimulationDataForWaterfall = (simulationsResult, selectedDataEntity) => {
  const dataType = selectedDataEntity === 0 ? sectionObjectKeys.DURATIONS : sectionObjectKeys.COSTS;
  const sections = selectedDataEntity === 0 ? simulationsResult?.section_durations : simulationsResult?.section_costs;

  const values = sections?.[dataType]?.mean?.map((item) =>
    dataType === sectionObjectKeys.DURATIONS ? item / HOURS_PER_DAY : item,
  );

  const totalSum = sum(values);
  const threshold = totalSum >= 10 ** 6;

  return { dataType, sections, values, totalSum, threshold };
};

export const normalizeValue = (value, threshold) => {
  return threshold ? round(parseFloat(value) / 1_000_000, 2) : parseFloat(value);
};

export const getPercentiles = (simulationResult, percentile, entity) => {
  const percentileIdx = simulationResult?.percentiles?.findIndex((value) => value === percentile);
  const entityMap = {
    time: 'cumulative_percentiles',
    cost: 'cumulative_cost_percentiles',
  };
  return simulationResult?.[entityMap[entity]]?.[percentileIdx];
};

export const getNumberValue = (value) =>
  displayNumber(round(value, 2), {
    scientific: false,
  });

export const getCostPercentile = (simulationResult, percentile) => {
  if (percentile === probabilities.MEAN) {
    return getNumberValue(simulationResult.average_cost);
  }
  const percentileIdx = simulationResult?.percentiles?.indexOf(percentile);

  if (percentileIdx >= 0) {
    return getNumberValue(simulationResult.cumulative_cost_percentiles[percentileIdx]);
  }
  return 0;
};

export const getCostItemOverviewTotal = (simulationResult) => {
  return {
    cells: [
      {},
      { value: i18n.t(translations.total) },
      { value: getCostPercentile(simulationResult, probabilities.MEAN) },
      { value: getCostPercentile(simulationResult, 10) },
      { value: getCostPercentile(simulationResult, 50) },
      { value: getCostPercentile(simulationResult, 90) },
    ],
  };
};

export const selectOptions = [
  {
    label: i18n.t(translations.simulations_cumulativeProbability),
    value: simulationChartType.CUMULATIVE_PROBABILITY,
  },
  {
    label: i18n.t(translations.simulations_resultPerDepth),
    value: simulationChartType.TIME_DEPTH,
  },
  {
    label: i18n.t(translations.simulations_sectionResults),

    value: simulationChartType.SECTION_DURATIONS,
  },
  {
    label: i18n.t(translations.simulations_operationResults),
    value: simulationChartType.OPERATION_DURATIONS,
  },
  {
    label: i18n.t(translations.simulations_activityResults),

    value: simulationChartType.ACTIVITY_DURATIONS,
  },
  {
    label: i18n.t(translations.simulations_costItemOverview),
    value: simulationChartType.COST_ITEM_OVERVIEW,
  },
  {
    label: i18n.t(translations.risks_risks),
    value: simulationChartType.RISKS,
  },
  {
    label: i18n.t(translations.simulations_projectModifiers),
    value: simulationChartType.MODIFIERS,
  },
  {
    label: i18n.t(translations.simulations_sectionWaterfall),
    value: simulationChartType.SECTION_WATERFALL,
  },
  {
    label: i18n.t(translations.simulations_probabilityDensity),
    value: simulationChartType.PROBABILITY_DENSITY,
  },
  {
    label: i18n.t(translations.projects_afeReport),
    value: simulationChartType.AFE_REPORT,
  },
];
